import { TFunction } from 'i18next';
import { FieldPath, UseFormReturn } from 'react-hook-form';

import { StepDefinition } from '@/components/organisms/StepWizard/StepWizard';
import { Webhook } from '@/shared/types/webhook';

import { SelectType } from './wizard/SelectType';
import { SetupWebhookStep } from './wizard/SetupWebhookStep';
import { WebhookWizardFormValues } from './wizardForm';

export function getWebhookWizardSteps(
  form: UseFormReturn<WebhookWizardFormValues>,
  t: TFunction,
  selectedType?: WebhookWizardFormValues['type'],
  webhooks?: Webhook[]
): StepDefinition[] {
  const typeTitleKey = (type?: WebhookWizardFormValues['type']): string => {
    switch (type) {
      case 'first_message':
        return 'webhook.type_first_message_title';
      case 'pre_message':
        return 'webhook.type_pre_message_title';
      case 'post_message':
        return 'webhook.type_post_message_title';
      case 'pre_nba':
        return 'webhook.type_pre_nba_title';
      case 'auth_validation':
        return 'webhook.type_auth_validation_title';
      case 'auth_pre_enter':
        return 'webhook.type_auth_pre_enter_title';
      case 'action':
      default:
        return 'webhook.type_dialog_title';
    }
  };

  const baseTypeLabel = t(typeTitleKey(selectedType));
  const includesWebhookWord = new RegExp(`(^|\\W)${t('webhook.noun')}(\\W|$)`, 'i').test(
    baseTypeLabel
  );
  const composedTypeLabel = includesWebhookWord
    ? baseTypeLabel
    : `${baseTypeLabel} ${t('webhook.noun')}`;

  const setupTitle = t('webhook.setup_step_title', { 0: composedTypeLabel });

  const headerFieldPaths = (): FieldPath<WebhookWizardFormValues>[] => {
    if (!form.getValues('includeHeaders')) {
      return [];
    }

    const headers = form.getValues('headers') ?? [];
    return headers.flatMap((_, index) => [
      `headers.${index}.name` as const,
      `headers.${index}.value` as const,
    ]);
  };

  return [
    {
      id: 'select_type',
      title: t('webhook.select_webhook_type', { defaultValue: 'Select webhook type' }),
      component: <SelectType existingWebhooks={webhooks} />,
      validate: () => form.trigger('type'),
    },
    {
      id: 'details',
      title: setupTitle,
      component: <SetupWebhookStep />,
      validate: () => form.trigger(['name', 'url', ...headerFieldPaths()]),
    },
  ];
}
